import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { managementReviewApi } from '../services/api';
import type { ManagementReview, ManagementReviewStatus } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';

// ── Section metadata ──────────────────────────────────────────────────────────

type SectionDef = {
  key: string;
  label: string;
  clause: string;
  canAi: boolean;
};

const INPUT_SECTIONS: SectionDef[] = [
  { key: 'previousActions',            label: 'Status of Previous Actions',            clause: 'ISO 27001 §9.3.2(a)', canAi: true  },
  { key: 'internalExternalChanges',    label: 'Internal & External Context Changes',    clause: 'ISO 27001 §9.3.2(b)', canAi: true  },
  { key: 'nonconformitiesCAs',         label: 'Nonconformities & Corrective Actions',   clause: 'ISO 27001 §9.3.2(c)', canAi: true  },
  { key: 'monitoringMetrics',          label: 'Monitoring & Measurement Results',       clause: 'ISO 27001 §9.3.2(d)', canAi: true  },
  { key: 'auditResults',               label: 'Audit Results',                          clause: 'ISO 27001 §9.3.2(e)', canAi: false },
  { key: 'objectivesStatus',           label: 'Information Security Objectives',        clause: 'ISO 27001 §9.3.2(f)', canAi: true  },
  { key: 'stakeholderFeedback',        label: 'Interested Parties Feedback',            clause: 'ISO 27001 §9.3.2(g)', canAi: false },
  { key: 'riskStatus',                 label: 'Risk Assessment & Treatment Status',     clause: 'ISO 27001 §9.3.2(h)', canAi: true  },
  { key: 'resourceAdequacy',           label: 'Adequacy of Resources',                  clause: 'ISO 27001 §9.3.2(i)', canAi: false },
  { key: 'improvementOpportunities',   label: 'Opportunities for Improvement',          clause: 'ISO 27001 §9.3.2(j)', canAi: true  },
];

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<ManagementReviewStatus, string> = {
  draft:     'np-chip np-tone-neutral',
  submitted: 'np-chip np-tone-warning',
  approved:  'np-chip np-tone-success',
};

const fmtDate = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── Citation helpers ──────────────────────────────────────────────────────────

type CitationItem = { type: string; id: string; label?: string };

const CITATION_ICON: Record<string, string> = {
  risk:        'ri-error-warning-line',
  control:     'ri-shield-check-line',
  evidence:    'ri-folder-check-line',
  issue:       'ri-bug-line',
  incident:    'ri-alarm-warning-line',
  document:    'ri-file-text-line',
  remediation: 'ri-tools-line',
  threat:      'ri-spy-line',
};

function parseCitations(rawText: string | null | undefined): { cleanText: string; citations: CitationItem[] } {
  if (!rawText) return { cleanText: '', citations: [] };
  const marker = rawText.indexOf('##CITATIONS##');
  if (marker === -1) return { cleanText: rawText.trim(), citations: [] };
  const cleanText = rawText.slice(0, marker).trim();
  const match = rawText.match(/##CITATIONS##\s*([\s\S]*?)\s*##END_CITATIONS##/);
  try {
    return { cleanText, citations: match ? JSON.parse(match[1]) : [] };
  } catch {
    return { cleanText, citations: [] };
  }
}

function markdownToHtml(text: string): string {
  if (!text) return '';
  // Already HTML — return as-is
  if (/<[a-z][\s\S]*>/i.test(text)) return text;

  const lines = text.split('\n');
  const html: string[] = [];
  let i = 0;

  const inlineFormat = (s: string) =>
    s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>');

  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines between blocks
    if (line.trim() === '') { i++; continue; }

    // Unordered list block
    if (/^(\s*[-*])\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^(\s*[-*])\s/.test(lines[i])) {
        items.push(`<li>${inlineFormat(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // Ordered list block
    if (/^\s*\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(`<li>${inlineFormat(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // Paragraph: collect consecutive non-blank, non-list lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(\s*[-*])\s/.test(lines[i]) &&
      !/^\s*\d+\.\s/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      html.push(`<p>${paraLines.map(inlineFormat).join('<br>')}</p>`);
    }
  }

  return html.join('');
}

// Alias used at call sites
const plainToHtml = markdownToHtml;

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SnapshotCard({ snapshot }: { snapshot: Record<string, any> }) {
  const risks    = snapshot.risks    || {};
  const controls = snapshot.controls || {};
  const evidence = snapshot.evidence || {};
  const issues   = snapshot.issues   || {};
  const incidents = snapshot.incidents || {};
  const objectives = snapshot.objectives || {};

  return (
    <div className="np-card h-100">
      <div className="np-card-head">
        <h2>
          <i className="ri-camera-line me-1"></i>
          ISMS Snapshot
        </h2>
        {snapshot.takenAt && (
          <span className="np-sub ms-auto">
            taken {fmtDateTime(snapshot.takenAt)}
          </span>
        )}
      </div>
      <div className="np-card-body">
        <div className="np-mini-stat-grid">
          {[
            { label: 'Risks open',          value: risks.open,           sub: `${risks.untreated ?? 0} untreated`,     cls: risks.untreated > 0 ? 'np-tone-danger' : '' },
            { label: 'Controls',            value: controls.total,       sub: `${controls.belowTarget ?? 0} below target`, cls: controls.belowTarget > 0 ? 'np-tone-warning' : '' },
            { label: 'Evidence overdue',    value: evidence.overdue,     sub: `/ ${evidence.total ?? 0} total`,        cls: evidence.overdue > 0 ? 'np-tone-danger' : '' },
            { label: 'Issues open',         value: issues.open,          sub: `${issues.closed ?? 0} closed`,         cls: '' },
            { label: 'Incidents open',      value: incidents.open,       sub: `${incidents.closed ?? 0} closed`,      cls: '' },
            { label: 'Objectives on track', value: objectives.onTrack,   sub: `${objectives.atRisk ?? 0} at risk`,    cls: '' },
          ].map(({ label, value, sub, cls }) => (
            <div className="np-mini-stat" key={label}>
              <div className={`np-mini-stat-value np-tabular ${cls}`}>{value ?? '—'}</div>
              <div className="np-mini-stat-label">{label}</div>
              <div className="np-mini-stat-sub">{sub}</div>
            </div>
          ))}
        </div>
        {snapshot.previousReviewId && (
          <div className="np-sub mt-2">
            <i className="ri-history-line me-1"></i>
            Previous review: <a href={`/management-reviews/${snapshot.previousReviewId}`}>{snapshot.previousReviewId}</a>
          </div>
        )}
      </div>
    </div>
  );
}

function InputSection({
  section,
  data,
  isApproved,
  onTextChange,
}: {
  section: SectionDef;
  data: { aiDraft?: string | null; humanText?: string; aiGeneratedAt?: string | null };
  isApproved: boolean;
  onTextChange: (key: string, text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const { citations }   = parseCitations(data.aiDraft);
  const editorValue     = plainToHtml(data.humanText || '');
  const isEmpty         = !data.humanText || data.humanText === '<p><br></p>';

  return (
    <div className="np-accordion-item">
      <button
        type="button"
        className="np-accordion-trigger"
        onClick={() => setOpen(!open)}
      >
        <span className="d-flex align-items-center gap-2 flex-wrap">
          <strong>{section.label}</strong>
          <span className="np-sub">{section.clause}</span>
          {!isEmpty
            ? <i className="ri-checkbox-circle-fill text-success small"></i>
            : data.aiDraft
            ? <span className="np-chip np-tone-primary">AI ready</span>
            : null
          }
        </span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-muted flex-shrink-0`}></i>
      </button>

      {open && (
        <div className="np-accordion-body">
          {isApproved ? (
            <div
              className="ql-editor border rounded bg-light px-3 py-2"
              style={{ minHeight: 80 }}
              dangerouslySetInnerHTML={{
                __html: editorValue || '<p style="color:var(--bs-secondary-color)">No content entered.</p>',
              }}
            />
          ) : (
            <>
              <style>{`#editor-${section.key} .ql-editor { min-height: 130px; }`}</style>
              <ReactQuill
                id={`editor-${section.key}`}
                theme="snow"
                value={editorValue}
                onChange={(val) => onTextChange(section.key, val)}
                modules={QUILL_MODULES}
                placeholder="Enter your notes for this section…"
              />
            </>
          )}

          {/* Citation chips */}
          {citations.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
              <span className="np-sub">Referenced:</span>
              {citations.map((c, i) => (
                <span
                  key={i}
                  className="np-chip np-tone-neutral"
                  title={c.id}
                >
                  <i className={CITATION_ICON[c.type] || 'ri-link-m'} />
                  {c.label || c.id}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Decision / action row ─────────────────────────────────────────────────────

function OutputList({
  title,
  items,
  columns,
  isApproved,
  onChange,
}: {
  title: string;
  items: Record<string, any>[];
  columns: { key: string; label: string; type?: 'text' | 'date' | 'select'; options?: string[] }[];
  isApproved: boolean;
  onChange: (items: Record<string, any>[]) => void;
}) {
  const addRow = () => {
    const blank: Record<string, any> = { id: crypto.randomUUID() };
    columns.forEach((c) => { blank[c.key] = ''; });
    onChange([...items, blank]);
  };

  const removeRow = (idx: number) => {
    const next = [...items];
    next.splice(idx, 1);
    onChange(next);
  };

  const updateCell = (idx: number, key: string, val: string) => {
    const next = items.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
    onChange(next);
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6>{title}</h6>
        {!isApproved && (
          <button className="np-btn np-btn-ghost np-btn-sm" onClick={addRow}>
            <i className="ri-add-line"></i>Add
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="np-sub">No items yet.</p>
      ) : (
        <div className="np-table-wrap">
          <table className="np-table">
            <thead>
              <tr>
                {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                {!isApproved && <th style={{ width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id || idx}>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {isApproved ? (
                        <span>{row[c.key] || '—'}</span>
                      ) : c.type === 'select' ? (
                        <select
                          className="np-select"
                          value={row[c.key] || ''}
                          onChange={(e) => updateCell(idx, c.key, e.target.value)}
                        >
                          <option value="">—</option>
                          {(c.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : c.type === 'date' ? (
                        <input
                          type="date"
                          className="np-input"
                          value={row[c.key] || ''}
                          onChange={(e) => updateCell(idx, c.key, e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="np-input"
                          value={row[c.key] || ''}
                          onChange={(e) => updateCell(idx, c.key, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  {!isApproved && (
                    <td className="text-center">
                      <button
                        className="np-icon-btn np-icon-btn-danger"
                        onClick={() => removeRow(idx)}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ManagementReviewDetail() {
  const { id }       = useParams<{ id: string }>();
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();
  const { confirm: swalConfirm } = useSwal();
  const [exporting, setExporting] = useState(false);
  // Local draft of inputs/outputs (saved on explicit Save)
  const [localInputs, setLocalInputs]   = useState<Record<string, any> | null>(null);
  const [localOutputs, setLocalOutputs] = useState<Record<string, any> | null>(null);

  const { data: review, isLoading, error } = useQuery<ManagementReview>({
    queryKey: ['management-reviews', id],
    queryFn:  () => managementReviewApi.get(id!),
    enabled:  !!id,
  });

  // Merge local edits on top of server data
  const inputs  = localInputs  ?? review?.inputs  ?? {};
  const outputs = localOutputs ?? review?.outputs ?? { decisions: [], ismsChanges: [], resourceNeeds: '', riskActions: [] };

  // ── Mutations ──────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (data: any) => managementReviewApi.update(id!, data),
    onSuccess: (_result, variables) => {
      // Patch the cache immediately so clearing local state doesn't flash stale data
      queryClient.setQueryData<ManagementReview>(['management-reviews', id], (old) =>
        old ? { ...old, inputs: variables.inputs, outputs: variables.outputs } : old
      );
      setLocalInputs(null);
      setLocalOutputs(null);
      // Background refetch to pick up any server-side changes
      queryClient.invalidateQueries({ queryKey: ['management-reviews', id] });
      toastSuccess('Review saved');
    },
    onError: () => toastError('Save failed'),
  });

  const submitMutation = useMutation({
    mutationFn: () => managementReviewApi.submit(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management-reviews', id] });
      queryClient.invalidateQueries({ queryKey: ['management-reviews'] });
      toastSuccess('Review submitted for approval');
    },
    onError: () => toastError('Submit failed'),
  });

  const approveMutation = useMutation({
    mutationFn: () => managementReviewApi.approve(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management-reviews', id] });
      queryClient.invalidateQueries({ queryKey: ['management-reviews'] });
      toastSuccess('Review approved');
    },
    onError: () => toastError('Approve failed'),
  });

  const returnMutation = useMutation({
    mutationFn: () => managementReviewApi.returnToDraft(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management-reviews', id] });
      queryClient.invalidateQueries({ queryKey: ['management-reviews'] });
      toastSuccess('Review returned to draft');
    },
    onError: () => toastError('Return to draft failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => managementReviewApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management-reviews'] });
      navigate('/management-reviews');
      toastSuccess('Review deleted');
    },
    onError: () => toastError('Delete failed'),
  });

  // ── Input section handlers ─────────────────────────────────────────────────

  const handleTextChange = useCallback((sectionKey: string, text: string) => {
    setLocalInputs((prev) => ({
      ...(prev ?? review?.inputs ?? {}),
      [sectionKey]: {
        ...((prev ?? review?.inputs ?? {})[sectionKey] ?? {}),
        humanText: text,
      },
    }));
  }, [review]);

  const handleSave = () => {
    saveMutation.mutate({ inputs, outputs });
  };

  const handleExportPdf = async () => {
    if (!review) return;
    setExporting(true);
    try {
      const { htmlToPdf } = await import('../utils/pdfExport');
      const ACCENT = '#4f46e5';
      const cs = (s: string) =>
        s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : '—';

      const statusMeta: Record<string, { bg: string; color: string; label: string }> = {
        draft:     { bg: '#fef3c7', color: '#d97706', label: 'Draft' },
        submitted: { bg: '#cffafe', color: '#0891b2', label: 'Submitted' },
        approved:  { bg: '#dcfce7', color: '#15803d', label: 'Approved' },
      };
      const sm = statusMeta[review.status] ?? { bg: '#f3f4f6', color: ACCENT, label: review.status };

      const snap  = (review.snapshot ?? {}) as Record<string, any>;
      const fmtD  = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

      // ── Helpers ───────────────────────────────────────────────────────────
      const stat = (label: string, val: string | number, sub?: string) =>
        `<div style="display:inline-block;width:calc(33% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;">
          <div style="font-size:20px;font-weight:700;color:#111827;">${val}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px;">${label}</div>
          ${sub ? `<div style="font-size:10px;color:#ef4444;margin-top:2px;">${sub}</div>` : ''}
        </div>`;

      const sectionCard = (num: number, label: string, clause: string, body: string, aiAssisted: boolean) =>
        `<div style="margin-bottom:24px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <div style="display:flex;align-items:center;gap:12px;padding:12px 16px;background:#f9fafb;border-bottom:1px solid #e5e7eb;">
            <div style="width:28px;height:28px;border-radius:50%;background:${ACCENT};color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${num}</div>
            <div style="flex:1;">
              <div style="font-size:14px;font-weight:700;color:#111827;">${label}</div>
              <div style="font-size:11px;color:#6b7280;">${clause}</div>
            </div>
            ${aiAssisted ? `<div style="font-size:10px;color:#4f46e5;font-weight:600;">AI assisted</div>` : ''}
          </div>
          <div style="padding:16px 20px;font-size:13px;color:#111827;line-height:1.7;">${body || '<em style="color:#9ca3af;">No content recorded</em>'}</div>
        </div>`;

      const outputSectionCard = (num: number, label: string, body: string) =>
        `<div style="margin-bottom:24px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <div style="display:flex;align-items:center;gap:12px;padding:12px 16px;background:#f9fafb;border-bottom:1px solid #e5e7eb;">
            <div style="width:28px;height:28px;border-radius:50%;background:#15803d;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${num}</div>
            <div style="font-size:14px;font-weight:700;color:#111827;">${label}</div>
          </div>
          <div style="padding:16px 20px;">${body}</div>
        </div>`;

      const tableHtml = (items: any[], cols: { key: string; label: string }[]) => {
        if (!items?.length) return '<p style="font-size:12px;color:#9ca3af;font-style:italic;">No items recorded</p>';
        const hdr = cols.map(c =>
          `<th style="padding:6px 10px;background:#f9fafb;font-size:11px;font-weight:700;color:#374151;text-align:left;border:1px solid #e5e7eb;">${c.label}</th>`
        ).join('');
        const rows = items.map(row =>
          `<tr>${cols.map(c =>
            `<td style="padding:6px 10px;border:1px solid #e5e7eb;font-size:12px;color:#374151;vertical-align:top;">${cs(row[c.key] || '')}</td>`
          ).join('')}</tr>`
        ).join('');
        return `<table style="width:100%;border-collapse:collapse;"><thead><tr>${hdr}</tr></thead><tbody>${rows}</tbody></table>`;
      };

      // ── TOC ───────────────────────────────────────────────────────────────
      const tocSections = [
        ...INPUT_SECTIONS.map((s, i) => ({ num: i + 1, label: s.label, clause: s.clause, color: ACCENT })),
        { num: 11, label: 'Decisions & Actions',  clause: 'ISO 27001 §9.3.3', color: '#15803d' },
        { num: 12, label: 'ISMS Changes',          clause: 'ISO 27001 §9.3.3', color: '#15803d' },
        { num: 13, label: 'Resource Needs',        clause: 'ISO 27001 §9.3.3', color: '#15803d' },
        { num: 14, label: 'Risk Actions',          clause: 'ISO 27001 §9.3.3', color: '#15803d' },
      ];
      const tocHtml = tocSections.map(({ num, label, clause, color }) =>
        `<div style="display:flex;align-items:center;padding:8px 0;border-bottom:1px solid #f3f4f6;">
          <span style="width:26px;height:26px;display:inline-flex;align-items:center;justify-content:center;background:${color};color:#fff;border-radius:50%;font-size:11px;font-weight:700;flex-shrink:0;">${num}</span>
          <span style="flex:1;font-size:13px;color:#111827;margin-left:12px;">${label}</span>
          <span style="font-size:11px;color:#9ca3af;">${clause}</span>
        </div>`
      ).join('');

      // ── Input sections ────────────────────────────────────────────────────
      const inputHtml = INPUT_SECTIONS.map((s, idx) => {
        const d = inputs[s.key] ?? {};
        return sectionCard(idx + 1, s.label, s.clause, d.humanText || '', !!d.aiGeneratedAt);
      }).join('');

      // ── Output sections ───────────────────────────────────────────────────
      const out = outputs;
      const decisionsHtml = tableHtml(out.decisions ?? [], [
        { key: 'description', label: 'Decision / Action' },
        { key: 'owner',       label: 'Owner' },
        { key: 'dueDate',     label: 'Due Date' },
        { key: 'status',      label: 'Status' },
      ]);
      const ismsChangesHtml = tableHtml(out.ismsChanges ?? [], [
        { key: 'change',    label: 'Change' },
        { key: 'rationale', label: 'Rationale' },
        { key: 'owner',     label: 'Owner' },
        { key: 'status',    label: 'Status' },
      ]);
      const riskActionsHtml = tableHtml(out.riskActions ?? [], [
        { key: 'risk',   label: 'Risk' },
        { key: 'action', label: 'Action' },
        { key: 'owner',  label: 'Owner' },
        { key: 'status', label: 'Status' },
      ]);
      const resourceNeedsHtml = out.resourceNeeds
        ? `<div style="font-size:13px;color:#111827;line-height:1.7;white-space:pre-wrap;">${cs(out.resourceNeeds)}</div>`
        : '<p style="font-size:12px;color:#9ca3af;font-style:italic;">No content recorded</p>';

      const html = `
<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">

  <!-- Cover -->
  <div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
    <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">ISO 27001 Clause 9.3</div>
    <h1 style="font-size:26px;font-weight:800;color:#111827;margin:0 0 14px 0;">${cs(review.title)}</h1>
    <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:24px;">
      <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:${sm.color};background:${sm.bg};">${sm.label}</span>
      <span style="font-size:13px;color:#6b7280;">${cs(review.period)}</span>
      ${review.meetingDate ? `<span style="font-size:13px;color:#6b7280;">· Meeting: ${fmtD(review.meetingDate)}</span>` : ''}
      ${review.reviewStartDate ? `<span style="font-size:13px;color:#6b7280;">· Period from: ${fmtD(review.reviewStartDate)}</span>` : ''}
      ${review.approvedAt ? `<span style="font-size:13px;color:#15803d;">· Approved ${fmtD(review.approvedAt)}</span>` : ''}
    </div>
    <div style="display:flex;gap:16px;flex-wrap:wrap;">
      ${stat('Risks (open)', snap.risks?.open ?? '—', snap.risks?.untreated ? `${snap.risks.untreated} untreated` : undefined)}
      ${stat('Controls below target', snap.controls?.belowTarget ?? '—')}
      ${stat('Evidence overdue', snap.evidence?.overdue ?? '—')}
      ${stat('Issues open', snap.issues?.open ?? '—')}
      ${stat('Objectives on track', snap.objectives?.onTrack ?? '—')}
      ${stat('Remediations overdue', snap.remediations?.overdue ?? '—')}
    </div>
  </div>

  <!-- Table of Contents -->
  <div style="margin-bottom:32px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
    <div style="padding:14px 20px;background:#f9fafb;border-bottom:1px solid #e5e7eb;">
      <h2 style="font-size:16px;font-weight:700;color:#111827;margin:0;">Table of Contents</h2>
    </div>
    <div style="padding:8px 20px 4px 20px;">${tocHtml}</div>
  </div>

  <!-- Input Sections heading -->
  <div style="margin-bottom:16px;">
    <h2 style="font-size:18px;font-weight:700;color:#111827;margin:0 0 4px 0;">Review Inputs</h2>
    <div style="font-size:12px;color:#6b7280;">ISO 27001 \u00a79.3.2 \u2014 items considered during this review</div>
  </div>
  ${inputHtml}

  <!-- Output Sections heading -->
  <div style="margin:32px 0 16px 0;">
    <h2 style="font-size:18px;font-weight:700;color:#111827;margin:0 0 4px 0;">Review Outputs</h2>
    <div style="font-size:12px;color:#6b7280;">ISO 27001 \u00a79.3.3 \u2014 decisions and improvement actions</div>
  </div>
  ${outputSectionCard(11, 'Decisions & Actions', decisionsHtml)}
  ${outputSectionCard(12, 'ISMS Changes', ismsChangesHtml)}
  ${outputSectionCard(13, 'Resource Needs', resourceNeedsHtml)}
  ${outputSectionCard(14, 'Risk Actions', riskActionsHtml)}

  <!-- Footer -->
  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;">
    <span style="font-size:11px;color:#9ca3af;">${cs(review.reviewId)}</span>
    <span style="font-size:11px;color:#9ca3af;">Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
  </div>
</div>`;

      await htmlToPdf(html, `${review.reviewId}-management-review.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async () => {
    // Auto-save first if there are local changes
    if (localInputs || localOutputs) {
      await saveMutation.mutateAsync({ inputs, outputs });
    }
    submitMutation.mutate();
  };

  const handleDelete = async () => {
    const confirmed = await swalConfirm('Delete review?', 'This cannot be undone.');
    if (confirmed) deleteMutation.mutate();
  };

  // ── Output helpers ─────────────────────────────────────────────────────────

  const setOutputField = (field: string, value: any) => {
    setLocalOutputs((prev) => ({ ...(prev ?? review?.outputs ?? {}), [field]: value }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          Failed to load management review.
        </div>
        <button className="np-btn np-btn-ghost" onClick={() => navigate('/management-reviews')}>
          Back to list
        </button>
      </div>
    );
  }

  const isApproved  = review.status === 'approved';
  const isSubmitted = review.status === 'submitted';
  const isDraft     = review.status === 'draft';
  const hasLocalEdits = !!(localInputs || localOutputs);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-clipboard-line"
        title={review.title}
        subtitle={review.period || ''}
        breadcrumbs={[
          { label: 'Management Reviews', to: '/management-reviews' },
          { label: review.reviewId },
        ]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="np-btn np-btn-ghost"
              onClick={handleExportPdf}
              disabled={exporting}
              title="Export to PDF"
            >
              {exporting
                ? <><span className="spinner-border spinner-border-sm me-1" />Exporting…</>
                : <><i className="ri-file-pdf-line"></i>Export PDF</>}
            </button>
            {/* Save only when there are unsaved local edits */}
            {(isDraft || isSubmitted) && (
              <button
                className={`np-btn ${hasLocalEdits ? 'np-btn-primary' : 'np-btn-ghost'}`}
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending
                  ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                  : hasLocalEdits ? 'Save Changes' : 'Save'}
              </button>
            )}
            {isDraft && (
              <>
                <button
                  className="np-btn np-btn-warning"
                  onClick={handleSubmit}
                  disabled={submitMutation.isPending || saveMutation.isPending}
                >
                  Submit for Review
                </button>
                <button
                  className="np-btn np-btn-ghost-danger"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </button>
              </>
            )}
            {isSubmitted && (
              <>
                <button
                  className="np-btn np-btn-success"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isPending}
                >
                  {approveMutation.isPending
                    ? <><span className="spinner-border spinner-border-sm me-1" />Approving…</>
                    : 'Approve'}
                </button>
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => returnMutation.mutate()}
                  disabled={returnMutation.isPending}
                >
                  Return to Draft
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Top row: metadata + snapshot */}
      <div className="np-grid np-grid-12-4-8 mb-4">
        <div className="np-card h-100">
          <div className="np-card-body">
            <div>
              <div className="np-meta-row">
                <span className="np-meta-label">ID</span>
                <span className="np-meta-value np-tabular"><code>{review.reviewId}</code></span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label">Period</span>
                <span className="np-meta-value">{review.period || '—'}</span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label">Meeting Date</span>
                <span className="np-meta-value np-tabular">{fmtDate(review.meetingDate)}</span>
              </div>
              {review.reviewStartDate && (
                <div className="np-meta-row">
                  <span className="np-meta-label">Review Start</span>
                  <span className="np-meta-value np-tabular">{fmtDate(review.reviewStartDate)}</span>
                </div>
              )}
              <div className="np-meta-row">
                <span className="np-meta-label">Status</span>
                <span className={STATUS_BADGE[review.status]}>
                  {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                </span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label">Created</span>
                <span className="np-meta-value np-tabular">{fmtDate(review.createdAt)}</span>
              </div>
              {review.submittedAt && (
                <div className="np-meta-row">
                  <span className="np-meta-label">Submitted</span>
                  <span className="np-meta-value np-tabular">{fmtDateTime(review.submittedAt)}</span>
                </div>
              )}
              {review.approvedAt && (
                <div className="np-meta-row">
                  <span className="np-meta-label">Approved</span>
                  <span className="np-meta-value np-tabular">{fmtDateTime(review.approvedAt)}</span>
                </div>
              )}
            </div>
            {review.previousReviewOutputs && Object.keys(review.previousReviewOutputs).length > 0 && (
              <div className="np-banner np-tone-neutral mt-3">
                <i className="ri-history-line"></i>
                <span>
                  <strong>Previous review</strong>{review.previousReviewTitle ? `: ${review.previousReviewTitle}` : ''}<br />
                  {(review.previousReviewOutputs.decisions || []).length > 0 && (
                    <span>{review.previousReviewOutputs.decisions.length} decision(s) · </span>
                  )}
                  {(review.previousReviewOutputs.riskActions || []).length > 0 && (
                    <span>{review.previousReviewOutputs.riskActions.length} risk action(s)</span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
        <div>
          <SnapshotCard snapshot={review.snapshot || {}} />
        </div>
      </div>

      <div>
          {/* Inputs accordion */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <div>
                <h2><i className="ri-file-list-3-line text-primary"></i> Review Inputs</h2>
                <p className="np-sub mb-0 mt-1">ISO 27001 §9.3.2 — all items must be considered</p>
              </div>
            </div>
            <div className="np-card-body">
              {isApproved && (
                <div className="np-banner np-tone-success mb-3">
                  <i className="ri-lock-line"></i>
                  This review is approved — content is read-only.
                </div>
              )}
              {INPUT_SECTIONS.map((section) => (
                <InputSection
                  key={section.key}
                  section={section}
                  data={inputs[section.key] || {}}
                  isApproved={isApproved}
                  onTextChange={handleTextChange}
                />
              ))}
            </div>
          </div>

          {/* Outputs */}
          <div className="np-card">
            <div className="np-card-head">
              <div>
                <h2><i className="ri-checkbox-multiple-line text-success"></i> Review Outputs</h2>
                <p className="np-sub mb-0 mt-1">ISO 27001 §9.3.3 — decisions and improvement actions</p>
              </div>
            </div>
            <div className="np-card-body">
              <OutputList
                title="Decisions & Actions"
                items={outputs.decisions || []}
                isApproved={isApproved}
                columns={[
                  { key: 'description', label: 'Decision / Action' },
                  { key: 'owner',       label: 'Owner' },
                  { key: 'dueDate',     label: 'Due Date', type: 'date' },
                  { key: 'status',      label: 'Status', type: 'select', options: ['open', 'in progress', 'complete', 'cancelled'] },
                ]}
                onChange={(items) => setOutputField('decisions', items)}
              />

              <OutputList
                title="ISMS Changes"
                items={outputs.ismsChanges || []}
                isApproved={isApproved}
                columns={[
                  { key: 'description', label: 'Change Description' },
                  { key: 'type',        label: 'Type', type: 'select', options: ['policy', 'control', 'scope', 'procedure', 'objective', 'other'] },
                ]}
                onChange={(items) => setOutputField('ismsChanges', items)}
              />

              <div className="mb-4">
                <h6>Resource Needs</h6>
                {isApproved ? (
                  <div
                    className="ql-editor border rounded bg-light px-3 py-2"
                    style={{ minHeight: 64 }}
                    dangerouslySetInnerHTML={{
                      __html: plainToHtml(outputs.resourceNeeds || '') || '<p style="color:var(--bs-secondary-color)">None recorded.</p>',
                    }}
                  />
                ) : (
                  <>
                    <style>{`.resource-needs-editor .ql-editor { min-height: 80px; }`}</style>
                    <ReactQuill
                      className="resource-needs-editor"
                      theme="snow"
                      value={plainToHtml(outputs.resourceNeeds || '')}
                      onChange={(val) => setOutputField('resourceNeeds', val)}
                      modules={QUILL_MODULES}
                      placeholder="Describe any additional resources identified as needed…"
                    />
                  </>
                )}
              </div>

              <OutputList
                title="Risk Treatment Actions"
                items={outputs.riskActions || []}
                isApproved={isApproved}
                columns={[
                  { key: 'description', label: 'Action' },
                  { key: 'owner',       label: 'Owner' },
                  { key: 'dueDate',     label: 'Due Date', type: 'date' },
                  { key: 'status',      label: 'Status', type: 'select', options: ['open', 'in progress', 'complete', 'cancelled'] },
                ]}
                onChange={(items) => setOutputField('riskActions', items)}
              />
            </div>
          </div>
      </div>
    </div>
  );
}
